import notistack from "pages/Core/Notistack";
import React, { useState, useCallback, useEffect } from "react";
import { Modal } from "react-bootstrap";
import { completeReturnSalesOrderQC } from "../services/return.sales.service";
import { HttpStatus } from "pages/Core";
import { useNavigate } from "react-router-dom";
import Loader from "Common/Loader";

interface ModalProps {
    show: boolean;
    onClose: () => void;
    returnData: any;
    items: any[];
    returnId: number;
    returnIds: { salesOrderItemId: number }[];
}

const ReturnCalculationModal: React.FC<ModalProps> = ({
    show,
    onClose,
    returnData,
    items,
    returnId,
    returnIds
}) => {
    const navigate = useNavigate()

    const [gridData, setGridData] = useState<any[]>([]);
    const [returnPrice, setReturnPrice] = useState<string>("");
    const [returnSaleType, setReturnSaleType] = useState<string>("");
    const [customerBankDetails, setCustomerBankDetails] = useState<any>(null);

    useEffect(() => {
        if (items && items.length > 0) {
            const initializedData = items.map(item => ({
                ...item,
                returnSaleOrderAmount: item.returnSaleOrderAmount || item.saleOrderAmount || 0,
                qcStatus: item.qcStatus || 1
            }));
            setGridData(initializedData);
        }

        setCustomerBankDetails(returnData?.customerBankDetails || null);

        if (returnData?.returnSalesOrderModel?.totalReturnSaleOrderAmount) {
            setReturnSaleType(returnData.returnSalesOrderModel?.returnSaleType || "");
            setReturnPrice(returnData.returnSalesOrderModel.totalReturnSaleOrderAmount.toFixed(2));
        }
    }, [items, returnData]);

    const formatValue = (val: number) => val ? val.toFixed(2) : "0.00";

    const qcStatusOptions = [
        { id: 1, name: "Complete QC" },
        { id: 2, name: "Partial QC" }
    ];

    const handlePriceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        const regex = /^\d*\.?\d{0,2}$/;

        if (regex.test(value)) {
            setReturnPrice(value);
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (["-", "+", "e", "E"].includes(e.key)) {
            e.preventDefault();
        }
    };

    const handleItemReturnPriceChange = (itemId: number, value: string) => {
        const regex = /^\d*\.?\d{0,2}$/;

        if (regex.test(value)) {
            const updatedData = gridData.map(item =>
                item.id === itemId
                    ? { ...item, returnSaleOrderAmount: value === "" ? 0 : parseFloat(value) }
                    : item
            );
            setGridData(updatedData);

            const newTotal = updatedData.reduce((sum, item) => {
                const price = parseFloat(item.returnSaleOrderAmount) || 0;
                return sum + price;
            }, 0);
            setReturnPrice(newTotal.toFixed(2));
        }
    };

    const handleQCStatusChange = (itemId: number, statusId: number) => {
        const updatedData = gridData.map(item =>
            item.id === itemId
                ? { ...item, qcStatus: statusId }
                : item
        );
        setGridData(updatedData);
    };

    const [isSubmitting, setIsSubmitting] = useState(false);
    const handleSubmit = async () => {
        const invalidItems = gridData.filter(item =>
            item?.returnSaleOrderAmount === undefined ||
            item?.returnSaleOrderAmount === null ||
            isNaN(item?.returnSaleOrderAmount) ||
            item?.returnSaleOrderAmount < 0
        );

        if (invalidItems.length > 0) {
            notistack.warning("Please enter valid return prices for all items");
            return;
        }

        const payload = {
            returnSalesOrderId: returnId,
            totalReturnSalesPrice: parseFloat(returnPrice) || 0,
            qcItems: gridData.map((item: any, i) => ({
                returnSalesOrderItemId: returnIds[i].salesOrderItemId,
                itemReturnPrice: parseFloat(item?.returnSaleOrderAmount) || 0,
                qcStatus: item?.qcStatus || 1
            }))
        };
        try {
            setIsSubmitting(true);
            const response = await completeReturnSalesOrderQC(payload);
            if (response.status === HttpStatus.Success) {
                notistack.notifyFromApi(response);
                onClose();
                navigate('/return-sales-order');
            } else {
                notistack.notifyFromApi(response);
            }
        } catch (error) {
            setIsSubmitting(false);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Modal show={show} onHide={onClose} size="xl" backdrop="static" centered>
            {isSubmitting && <Loader />}

            <Modal.Header closeButton>
                <Modal.Title>Return Calculation</Modal.Title>
            </Modal.Header>

            <Modal.Body>
                <div className="row mb-3">
                    <div className="col-md-6">
                        <h6 className="fw-bold">Edit Price</h6>

                        <div className="mb-2 w-50">
                            <label>
                                Total Return Price <span className="text-danger">*</span>
                            </label>

                            <input
                                type="text"
                                className="form-control form-control-sm"
                                value={returnPrice}
                                onChange={handlePriceChange}
                                onKeyDown={handleKeyDown}
                                placeholder="Enter return price"
                                style={{ textAlign: "right" }}
                            />
                        </div>
                        <div className="mt-3">
                            <h6 className="fw-bold">Customer Bank Details</h6>

                            {customerBankDetails ? (
                                <div className="card shadow-sm border-0">
                                    <div className="card-body p-3">
                                        <div className="row mb-2">
                                            <div className="col-5 text-muted small">Bank Name</div>
                                            <div className="col-7 fw-semibold">
                                                {customerBankDetails?.bankName || "-"}
                                            </div>
                                        </div>

                                        <div className="row mb-2">
                                            <div className="col-5 text-muted small">Account Holder</div>
                                            <div className="col-7 fw-semibold">
                                                {customerBankDetails?.accountHolderName || "-"}
                                            </div>
                                        </div>

                                        <div className="row mb-2">
                                            <div className="col-5 text-muted small">Account Number</div>
                                            <div className="col-7 fw-semibold">
                                                {customerBankDetails?.accountNo || "-"}
                                            </div>
                                        </div>

                                        <div className="row">
                                            <div className="col-5 text-muted small">IFSC Code</div>
                                            <div className="col-7 fw-semibold">
                                                {customerBankDetails?.ifsc || "-"}
                                            </div>
                                        </div>
                                        {customerBankDetails?.url && (
                                            <div className="row mt-1">
                                                <div className="col-5 text-muted small">Cancel Cheque</div>
                                                <div className="col-7 fw-semibold">
                                                    {<a href={customerBankDetails?.url} target="_blank" rel="noopener noreferrer">
                                                        view file
                                                    </a>}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ) : (
                                <div className="text-muted small">No bank details available</div>
                            )}
                        </div>
                    </div>

                    <div className="col-md-6">
                        <h6 className="fw-bold text-center">Deduction Summary</h6>

                        <div style={{ maxHeight: 250, overflow: "auto" }}>
                            {gridData.map((item: any, index: number) => (
                                <div key={item.id || index} className="card p-0 mb-0 shadow-sm border-0 rounded-3">
                                    <div className="card-body mb-0 p-2 border-bottom">
                                        <h6 className="fw-semibold">
                                            {item?.categoryName} (Style No: {item?.styleNo})
                                        </h6>
                                        <table className="table table-sm table-bordered mb-0">
                                            <thead>
                                                <tr>
                                                    <th style={{ width: "200px" }}>Header</th>
                                                    <th>Original Amt</th>
                                                    <th>{returnSaleType?.trim()} Amt</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {item?.deductionSummarys &&
                                                    Object.entries(item.deductionSummarys).map(([key, value]: [string, any]) => (
                                                        <tr key={key}>
                                                            <td><strong>{key}</strong></td>
                                                            <td>{formatValue(value?.orignalAmount)}</td>
                                                            <td>{formatValue(value?.returnAmount)}</td>
                                                        </tr>
                                                    ))}
                                                {/* {item?.deductionSummarys && (
                                                    <tr style={{ backgroundColor: "#f8f9fa", fontWeight: "bold", color: "red" }}>
                                                        <td>Total</td>
                                                        <td>
                                                            {formatValue(
                                                                Object.values(item.deductionSummarys).reduce(
                                                                    (sum: number, v: any) => sum + (v?.orignalAmount || 0),
                                                                    0
                                                                )
                                                            )}
                                                        </td>
                                                        <td>
                                                            {formatValue(
                                                                Object.values(item.deductionSummarys).reduce(
                                                                    (sum: number, v: any) => sum + (v?.returnAmount || 0),
                                                                    0
                                                                )
                                                            )}
                                                        </td>
                                                    </tr>
                                                )} */}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                <div className="table-responsive" style={{ maxHeight: 400, overflow: "auto" }}>
                    <table className="table table-bordered table-hover">
                        <thead>
                            <tr style={{ backgroundColor: "#b7002a", position: "sticky", top: 0, zIndex: 10 }}>
                                <th className="text-white">Style No.</th>
                                <th className="text-white">Category</th>
                                <th className="text-white">IGI No.</th>
                                <th className="text-white text-end">Net Wt</th>
                                <th className="text-white text-end">Gross Wt</th>
                                <th className="text-white text-end">Diamond Wt</th>
                                <th className="text-white text-end">Selling Price</th>
                                <th className="text-white text-end">Return Price</th>
                                <th className="text-white">QC Status</th>
                            </tr>
                        </thead>
                        <tbody>
                            {gridData.map((item) => (
                                <tr key={item.id}>
                                    <td>{item?.styleNo ?? "-"}</td>
                                    <td>{item?.categoryName ?? "-"}</td>
                                    <td>{item?.igiNo ?? "-"}</td>
                                    <td className="text-end">{item?.netWeight?.toFixed(3) || "0.000"}</td>
                                    <td className="text-end">{item?.grossWeight?.toFixed(3) || "0.000"}</td>
                                    <td className="text-end">{item?.diamondWeight?.toFixed(3) || "0.000"}</td>
                                    <td className="text-end">{item?.saleOrderAmount?.toFixed(2) || "0.00"}</td>
                                    <td className="text-end" style={{ minWidth: "130px" }}>
                                        <input
                                            type="text"
                                            className="form-control form-control-sm text-end"
                                            value={item?.returnSaleOrderAmount || ""}
                                            onChange={(e) => handleItemReturnPriceChange(item.id, e.target.value)}
                                            onKeyDown={handleKeyDown}
                                            placeholder="0.00"
                                            style={{ width: "130px" }}
                                        />
                                    </td>
                                    <td>
                                        <div className="d-flex gap-2">
                                            {qcStatusOptions.map(option => (
                                                <label key={option.id} className="d-flex align-items-center gap-1" style={{ cursor: "pointer" }}>
                                                    <input
                                                        type="radio"
                                                        name={`qc-status-${item.id}`}
                                                        value={option.id}
                                                        checked={(item?.qcStatus || 1) === option.id}
                                                        onChange={() => handleQCStatusChange(item.id, option.id)}
                                                    />
                                                    <span className="small">{option.name}</span>
                                                </label>
                                            ))}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </Modal.Body>

            <Modal.Footer className="d-flex justify-content-between">
                <div>
                    <strong>Total Return Amount: </strong>
                    ₹ {returnPrice || "0.00"}
                </div>

                <div className="d-flex gap-2">
                    <button className="btn add-btn" onClick={onClose}>
                        Cancel
                    </button>

                    <button className="btn submit-btn" onClick={handleSubmit} disabled={isSubmitting}>
                        Complete QC {isSubmitting && "..."}
                    </button>
                </div>
            </Modal.Footer>
        </Modal>
    );
};

export default ReturnCalculationModal;